// assets/component-bv-reviews.js
class BvReviews extends HTMLElement {
  constructor(){
    super();
    this._initialized = false;
    this._state = {
      offset: 0,
      total: 0,
      firstSummaryDone: false,
      filters: { ratingEq:'', cdv:{}, search:'' }
    };
    this._reviewsById = new Map();     // cache of reviews by Id for modal detail
    this._photoToReviewId = new Map(); // map photoKey -> reviewId
    this._themeModalReadyPromise = null;
  }

  connectedCallback(){
    if (this._initialized) return;
    this._readConfigFromDataset();
    this._cacheEls();
    this._attachEvents();
    this._initialized = true;
    this._init();

    if (!this._leftPhotosBound) {
      this._leftPhotosBound = true;
      this.el.list.addEventListener('click', (e)=> {
        const btn = e.target.closest('.bv-left-photo');
        if (!btn) return;
        const url = btn.getAttribute('data-full');
        const alt = btn.getAttribute('data-alt') || '';
        const rid = btn.getAttribute('data-review-id') || '';
        this._openImageModal(url, alt, rid);
      });
    }
  }

  /* ---------- Fetch helpers for modal ---------- */
  _fetchReviewById(id){
    const p = this._qs({
      ...this._commonParams(),
      resource: 'reviews',
      include: 'authors,products,comments',
      limit: 1
    });
    p.append('filter', `id:eq:${id}`);
    p.append('filter', `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`);
    return this._fetchJSON(`${this._cfg.BASE}/reviews.json?${p.toString()}`)
      .then(d => (d && d.Results && d.Results[0]) || null);
  }

  async _fetchReviewByPhotoUrl(photoUrl){
    // Try to find a review whose Photos array contains this URL (normalized)
    const target = this._normalizeUrl(photoUrl);
    // We'll fetch in pages to avoid huge payloads
    const PAGE = 20;
    let offset = 0;
    const MAX_PAGES = 3; // up to 60 reviews with media
    for (let i=0;i<MAX_PAGES;i++){
      const p = this._qs({
        ...this._commonParams(),
        resource: 'reviews',
        include: 'authors,products,comments',
        filteredstats: 'reviews',
        Stats: 'Reviews',
        sort: this._cfg.SORT,
        limit: PAGE,
        offset,
        limit_comments: this._cfg.COMMENTS_LIMIT
      });
      // same base filters + HasMedia
      this._buildFiltersArray({ forPhotos:true }).forEach(v => p.append('filter', v));
      p.append('filter_reviews', `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`);
      if (this._state.filters.search) p.append('Search', this._state.filters.search);

      const url = `${this._cfg.BASE}/reviews.json?${p.toString()}`;
      const data = await this._fetchJSON(url);
      const results = data?.Results || [];
      // scan for photo URL match
      for (const r of results){
        const photos = r.Photos || [];
        for (const ph of photos){
          const cand = this._normalizeUrl(ph?.Url || ph?.Sizes?.normal?.Url || ph?.Sizes?.thumbnail?.Url || '');
          if (cand && cand === target){
            // cache and return
            this._reviewsById.set(r.Id, r);
            (r.Photos||[]).forEach(pp=>{
              const key = this._photoKey(pp);
              if (key) this._photoToReviewId.set(key, r.Id);
            });
            return r;
          }
        }
      }
      offset += PAGE;
      const total = data?.TotalResults || 0;
      if (offset >= total) break;
    }
    return null;
  }

  _normalizeUrl(u){
    if (!u) return '';
    try {
      const url = new URL(u, window.location.origin);
      url.search = '';
      return url.href;
    } catch {
      return String(u).split('?')[0];
    }
  }

  _photoKey(p){
    const id = p?.Id || p?.PhotoId || p?.ContentId || '';
    if (id) return `id:${id}`;
    const url = p?.Url || p?.Sizes?.normal?.Url || p?.Sizes?.thumbnail?.Url || '';
    if (!url) return '';
    return `url:${this._normalizeUrl(url)}`;
  }

  _reviewIdFromPhoto(p){
    return p?.ReviewId || p?.ContentId || '';
  }

  _findReviewIdByImgSrc(src){
    if (!src) return '';
    const key = `url:${this._normalizeUrl(src)}`;
    return this._photoToReviewId.get(key) || '';
  }

  /* ---------- Config from data-* ---------- */
  _readConfigFromDataset(){
    const d = this.dataset;
    const num = (v,def)=> (v==null||v==='')?def:Number(v);
    const bool = (v,def)=> (v==null||v==='')?def:String(v).toLowerCase()==='true';
    this._cfg = {
      BASE: d.bvBase || 'https://apps.bazaarvoice.com/api/data',
      PASSKEY: d.passkey || '',
      API_VERSION: d.apiVersion || '5.5',
      DISPLAY_CODE: d.displayCode || '',
      PRODUCT_ID: d.productId || '',
      LOCALE: d.locale || 'en_US',
      THEME_MODAL_SRC: d.themeModalSrc || '',
      PAGE_SIZE: num(d.pageSize, 8),
      COMMENTS_LIMIT: num(d.commentsLimit, 3),
      MEDIA_LIMIT: num(d.mediaLimit, 50),
      STRIP_MAX: num(d.stripMax, 12),
      ENABLE_MEDIA_STRIP: bool(d.enableMediaStrip, true),
      SORT: d.sort || 'rating:desc,submissiontime:desc',
      ENABLE_JSONP_FALLBACK: bool(d.enableJsonp, true)
    };
  }

  /* ---------- DOM cache ---------- */
  _cacheEls(){
    this._root = this.querySelector('#bv-reviews') || this;
    const $ = (sel, root=this._root)=> root.querySelector(sel);
    this.$ = $;

    this.el = {
      filtersRoot: $('#bv-filters'),
      list:       $('#bv-list'),
      strip:      this._root.querySelector('.bv-media-strip'),
      empty:      this._root.querySelector('.bv-empty'),
      error:      this._root.querySelector('.bv-error'),
      loadMore:   this._root.querySelector('#bv-load-more'),
      search:     this._root.querySelector('#bv-search'),
      stars:      this._root.querySelector('.bv-stars'),
      avg:        this._root.querySelector('.bv-average'),
      count:      this._root.querySelector('.bv-count'),
      stripContainer: this._root.querySelector('.bv-media-strip-container'),
      stripLeft:     this._root.querySelector('.bv-strip-arrow--left'),
      stripRight:    this._root.querySelector('.bv-strip-arrow--right'),
    };
  }

  /* ---------- Events ---------- */
  _attachEvents(){
    this.el.search?.addEventListener('input', this._debounce(()=>{
      this._state.filters.search = this.el.search.value.trim();
      this._resetAndReload();
    }, 1000));

    this.el.loadMore?.addEventListener('click', ()=>{
      if (this.el.loadMore.disabled) return;
      this._loadMore();
    });
  }

  /* ---------- Init ---------- */
  async _init(){
    await this._buildFiltersUI();
    await this._loadMore();
    this._initStripArrows();
  }

  /* ---------- Network Helpers ---------- */
  _qs(params){
    const u = new URLSearchParams();
    Object.entries(params).forEach(([k,v])=>{
      if (v!==undefined && v!==null && v!=='') u.append(k,v);
    });
    return u;
  }
  async _fetchJSON(url){
    try{
      const r = await fetch(url);
      if(!r.ok) throw new Error(r.statusText);
      return await r.json();
    }catch(e){
      if(!this._cfg.ENABLE_JSONP_FALLBACK) throw e;
      return this._jsonp(url);
    }
  }
  _jsonp(url){
    return new Promise((resolve,reject)=>{
      const cb = 'bv_cb_' + Math.random().toString(36).slice(2);
      const sep = url.includes('?')?'&':'?';
      const s = document.createElement('script');
      s.onerror = ()=>{ cleanup(); reject(new Error('JSONP error')); };
      window[cb] = (d)=>{ cleanup(); resolve(d); };
      s.src = `${url}${sep}callback=${cb}`;
      document.head.appendChild(s);
      function cleanup(){ delete window[cb]; s.remove(); }
    });
  }

  /* ---------- URLs ---------- */
  _commonParams(){
    return {
      passkey: this._cfg.PASSKEY,
      apiversion: this._cfg.API_VERSION,
      displaycode: this._cfg.DISPLAY_CODE
    };
  }
  _buildFiltersArray({ forPhotos=false } = {}){
    const f = [];
    f.push(`productid:eq:${this._cfg.PRODUCT_ID}`);
    f.push(`contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`);
    f.push(`isratingsonly:eq:false`);
    if (this._state.filters.ratingEq) f.push(`rating:eq:${this._state.filters.ratingEq}`);
    Object.entries(this._state.filters.cdv).forEach(([id,val])=>{
      if (val) f.push(`contextdatavalue_${id}:eq:${val}`);
    });
    if (forPhotos) f.push(`HasMedia:eq:true`);
    return f;
  }
  _buildReviewsURL(offset=0){
    const p = this._qs({
      ...this._commonParams(),
      resource: 'reviews',
      action: 'REVIEWS_N_STATS',
      include: 'authors,products,comments',
      filteredstats: 'reviews',
      Stats: 'Reviews',
      sort: this._cfg.SORT,
      limit: this._cfg.PAGE_SIZE,
      offset,
      limit_comments: this._cfg.COMMENTS_LIMIT
    });
    this._buildFiltersArray().forEach(v => p.append('filter', v));
    p.append('filter_reviews', `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`);
    if (this._state.filters.search) p.append('Search', this._state.filters.search);
    return `${this._cfg.BASE}/reviews.json?${p.toString()}`;
  }
  _buildPhotosURL(){
    const p = this._qs({
      ...this._commonParams(),
      resource: 'reviews',
      action: 'PHOTOS_TYPE',
      include: 'authors,products,comments',
      filteredstats: 'reviews',
      Stats: 'Reviews',
      sort: this._cfg.SORT,
      limit: this._cfg.MEDIA_LIMIT,
      offset: 0,
      limit_comments: this._cfg.COMMENTS_LIMIT
    });
    this._buildFiltersArray({ forPhotos:true }).forEach(v => p.append('filter', v));
    p.append('filter_reviews', `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`);
    if (this._state.filters.search) p.append('Search', this._state.filters.search);
    return `${this._cfg.BASE}/reviews.json?${p.toString()}`;
  }
  _buildProductURL(){
    const p = this._qs({
      ...this._commonParams(),
      resource: 'products',
      filter: `id:eq:${this._cfg.PRODUCT_ID}`,
      filter_reviews: `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`,
      filter_questions: `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`,
      filter_answers: `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`,
      filter_reviewcomments: `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`,
      filteredstats: 'Reviews, questions, answers',
      stats: 'Reviews, questions, answers'
    });
    return `${this._cfg.BASE}/products.json?${p.toString()}`;
  }

  /* ---------- Render helpers ---------- */
  _renderStars(target, value){
    const v = Number(value)||0, full=Math.floor(v), half=v-full>=.5?1:0, empty=5-full-half;
    target.textContent = '★'.repeat(full)+(half?'☆':'')+'☆'.repeat(empty);
    target.setAttribute('aria-label', `${v.toFixed(1)} out of 5`);
  }
  _escapeHtml(s){return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;').replace(/_/g,' ');}
  _timeAgo(iso){
    const d=new Date(iso);const sec=Math.max(1,Math.floor((Date.now()-d)/1000));
    const steps=[[60,'second'],[60,'minute'],[24,'hour'],[7,'day'],[4.348,'week'],[12,'month'],[1/0,'year']];
    let n=sec,u='second';for(const [s,l] of steps){if(n<s){u=l;break}n=Math.floor(n/s);u=l}return `${n} ${u}${n>1?'s':''} ago`;
  }
  _buildLeft(review){
    const user = review.UserNickname || 'Anonymous';
    const cdvs = review.ContextDataValues || {};
    const pairs = Object.keys(cdvs).map(k=>{
      const label = (cdvs[k]?.DimensionLabel) || k.replace(/([a-z])([A-Z])/g,'$1 $2').toUpperCase();
      const value = cdvs[k]?.ValueLabel || cdvs[k]?.Value;
      return value ? [label, value] : null;
    }).filter(Boolean);

    const photos = (review.Photos || []).slice(0, 2);
    const photosHtml = photos.length ? `
      <div class="bv-left-photos">
        ${photos.map(p=>{
          const thumb = (p.Sizes?.thumbnail?.Url || p.Sizes?.normal?.Url || p.Url || '');
          const full  = (p.Sizes?.normal?.Url || p.Url || thumb);
          const alt   = this._escapeHtml(p.Caption || 'review photo');
          return `<button type="button" class="bv-left-photo" data-full="${full}" data-alt="${alt}" data-review-id="${review.Id || ''}">
                    <img src="${thumb}" loading="lazy" alt="${alt}">
                  </button>`
        }).join('')}
      </div>` : '';

    return `
      <div class="bv-left">
        <div class="bv-user">${this._escapeHtml(user)}</div>
        ${pairs.length ? `<dl class="bv-attrs">${pairs.map(([k,v])=>`<div><dt>${this._escapeHtml(k)}:</dt><dd>${this._escapeHtml(v||'')}</dd></div>`).join('')}</dl>` : ''}
        ${photosHtml}
      </div>
    `;
  }
  _buildRight(review){
    const rating = review.Rating||0;
    const title = review.Title||'';
    const text = review.ReviewText||'';
    return `
      <div class="bv-right">
        <div class="bv-rating-line"><span class="bv-stars small">${'★'.repeat(rating)}${'☆'.repeat(5-rating)}</span></div>
        ${title?`<h3 class="bv-review-title">${this._escapeHtml(title)}</h3>`:''}
        <div class="bv-meta">${this._timeAgo(review.SubmissionTime)}</div>
        ${text?`<p class="bv-text">${this._escapeHtml(text)}</p>`:''}
      </div>
    `;
  }
  _renderReviews(results){
    const frag = document.createDocumentFragment();
    (results||[]).forEach(r=>{
      const li=document.createElement('li');
      li.className='bv-item';
      li.innerHTML = this._buildLeft(r)+this._buildRight(r);

      this._reviewsById.set(r.Id, r);
      li.dataset.reviewId = r.Id || '';
      (r.Photos || []).forEach(p=>{
        const key = this._photoKey(p);
        if (key) this._photoToReviewId.set(key, r.Id);
      });
      frag.appendChild(li);
    });
    this.el.list.appendChild(frag);
  }
  _renderSummary(data){
    const prod = data?.Includes?.Products;
    let avg=0,count=0;
    if (prod) {
      const key = Object.keys(prod)[0];
      const st = prod[key]?.ReviewStatistics;
      if (st){ avg = st.AverageOverallRating||0; count = st.TotalReviewCount||data.TotalResults||0; }
    }
    if (!avg && data?.Results?.length){
      const arr=data.Results; avg = arr.reduce((s,r)=>s+(r.Rating||0),0)/arr.length;
    }
    this._renderStars(this.el.stars, avg);
    this.el.avg.textContent = avg?avg.toFixed(1):'';
    this.el.count.textContent = ` ${Number(data.TotalResults||count||0).toLocaleString()} Reviews`;
  }
  _renderStrip(results){
    if (!this._cfg.ENABLE_MEDIA_STRIP || !this.el.strip || !this.el.stripContainer) return;

    const photos = [];
    (results||[]).forEach(r => (r.Photos||[]).forEach(p => photos.push(p)));

    this.el.strip.innerHTML = photos.slice(0, this._cfg.STRIP_MAX).map(p=>{
      const thumb = (p.Sizes?.normal?.Url || p.Url || '');
      const full  = (p.Sizes?.normal?.Url || p.Url || thumb);
      const alt   = this._escapeHtml(p.Caption || 'review photo');
      const key   = this._photoKey(p);
      const rid   = this._reviewIdFromPhoto(p) || (key ? this._photoToReviewId.get(key) : '') || '';
      return `
        <button class="image" type="button"
                data-full="${full}" data-alt="${alt}"
                data-review-id="${rid}"
                data-photo-key="${key || ''}"
                data-photo-src="${this._normalizeUrl(full)}"
                aria-label="Open image">
          <img src="${thumb}" loading="lazy" alt="${alt}">
        </button>
      `;
    }).join('');

    this.el.stripContainer.hidden = photos.length === 0;
    if (this._updateStripArrows) this._updateStripArrows();

    if (!this._imageModal) this._ensureImageModal();

    if (!this._stripClickBound){
      this._stripClickBound = true;
      this.el.strip.addEventListener('click', async (e)=>{
        const btn = e.target.closest('.image');
        if (!btn) return;
        const url = btn.getAttribute('data-full');
        const alt = btn.getAttribute('data-alt') || '';
        let  rid = btn.getAttribute('data-review-id') || '';

        // Open modal immediately with image, then resolve review
        await this._openImageModal(url, alt, rid);

        // If no rid, try mapping and, if needed, fetch by photo URL
        if (!rid){
          const key = btn.getAttribute('data-photo-key') || '';
          if (key && this._photoToReviewId.has(key)) {
            rid = this._photoToReviewId.get(key);
          } else {
            const src = btn.getAttribute('data-photo-src') || url;
            const review = await this._fetchReviewByPhotoUrl(src);
            if (review){
              // Update right panel if it showed "not available"
              const livePanel = document.querySelector('#ThemeModal .bv-review-detail');
              if (livePanel && livePanel.innerHTML.includes('Review not available')){
                livePanel.innerHTML = this._buildReviewFull(review);
              }
              return; // done
            }
          }
        }

        // If we got rid after opening, fetch by id and update panel
        if (rid && !this._reviewsById.get(rid)){
          const review = await this._fetchReviewById(rid);
          if (review){
            const livePanel = document.querySelector('#ThemeModal .bv-review-detail');
            if (livePanel) livePanel.innerHTML = this._buildReviewFull(review);
          }
        }
      });
    }
  }

  /* ---------- Strip arrows (static outside scroller) ---------- */
  _initStripArrows(){
    if (!this.el.stripContainer) return;

    const onScrollOrResize = ()=> this._updateStripArrows();
    this.el.stripContainer.addEventListener('scroll', onScrollOrResize, { passive:true });
    window.addEventListener('resize', onScrollOrResize);

    this.el.stripLeft?.addEventListener('click', ()=> this._scrollStrip(-1));
    this.el.stripRight?.addEventListener('click', ()=> this._scrollStrip(1));

    this._updateStripArrows();
  }

  _updateStripArrows(){
    const s = this.el.stripContainer;
    if (!s) return;

    const max = Math.max(0, s.scrollWidth - s.clientWidth);
    const x   = Math.round(s.scrollLeft);

    const canLeft  = x > 0;
    const canRight = x < (max - 1);

    if (this.el.stripLeft)  this.el.stripLeft.hidden  = !canLeft;
    if (this.el.stripRight) this.el.stripRight.hidden = !canRight;

    const noOverflow = max <= 0 || s.offsetParent === null;
    if (noOverflow){
      if (this.el.stripLeft)  this.el.stripLeft.hidden  = true;
      if (this.el.stripRight) this.el.stripRight.hidden = true;
    }
  }

  _scrollStrip(dir){
    const s = this.el.stripContainer;
    if (!s) return;
    const step = s.clientWidth;
    s.scrollBy({ left: dir * step, behavior: 'smooth' });
    setTimeout(()=> this._updateStripArrows(), 350);
  }

  /* ---------- Modal ---------- */
  async _ensureThemeModalReady(){
    if (customElements?.get?.('theme-modal')) return true;

    if (!this._themeModalReadyPromise){
      this._themeModalReadyPromise = (async () => {
        const src = this._cfg?.THEME_MODAL_SRC || this.dataset?.themeModalSrc || '';
        let absoluteSrc = '';
        if (src) {
          try { absoluteSrc = new URL(src, window.location.origin).href; }
          catch { absoluteSrc = src; }
        }

        if (absoluteSrc) {
          const scripts = Array.from(document.querySelectorAll('script'));
          const alreadyPresent = scripts.some(s => s.src === absoluteSrc);
          if (!alreadyPresent) {
            await new Promise((resolve) => {
              const s = document.createElement('script');
              s.type = 'module';
              s.defer = true;
              s.src = absoluteSrc;
              s.dataset.bvThemeModal = 'true';
              const done = () => { s.dataset.bvThemeModalLoaded = 'true'; resolve(); };
              s.addEventListener('load', done, { once:true });
              s.addEventListener('error', () => {
                s.dataset.bvThemeModalLoaded = 'error';
                console.warn('BV Reviews: failed to load theme-modal script.');
                resolve();
              }, { once:true });
              document.head.appendChild(s);
            });
          }
        }

        if (customElements?.whenDefined) {
          try {
            await Promise.race([
              customElements.whenDefined('theme-modal'),
              new Promise(resolve => setTimeout(resolve, 2000))
            ]);
          } catch {
            /* noop */
          }
        }

        return customElements?.get?.('theme-modal') || null;
      })();
    }

    await this._themeModalReadyPromise;
    return customElements?.get?.('theme-modal') || null;
  }

  async _ensureImageModal(){
    if (this._imageModal) return this._imageModal;

    await this._ensureThemeModalReady();

    const modal = document.createElement('theme-modal');
    modal.setAttribute('data-focus-on-close', 'true');
    modal.setAttribute('data-section-id', this._root?.dataset?.wethemeSectionId || 'bv-reviews');
    modal.setAttribute('data-block-id', this._root?.dataset?.wethemeSectionId || 'bv-reviews');
    modal.setAttribute('data-width', 'max-content');

    modal.innerHTML = `
      <button type="button" data-modal-button style="position:absolute;width:0;height:0;padding:0;border:0;clip:rect(0 0 0 0);overflow:hidden;"></button>
      <template>
        <div data-modal-content class="theme-modal-content popup-shadow bv-image-modal">
          <button type="button" data-close class="button-unstyled icon-fallback-text standalone-icon--wrapper" aria-label="Close">
            <span class="fallback-text">Close</span>
            <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
          <div class="theme-modal--inner">
            <div data-modal-body class="modal-content">
              <div class="bv-modal-grid">
                <div class="bv-modal-left" style="flex:1 1 50%;min-width:0;display:flex;justify-content:center;align-items:center;">
                  <img class="bv-image-full" src="" alt="" style="max-width:100%;max-height:80vh;object-fit:contain;" />
                </div>
                <div class="bv-modal-right" style="flex:1 1 50%;min-width:0;overflow:auto;max-height:80vh;">
                  <div class="bv-review-detail"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    `;

    if (typeof modal.cacheDOMElements === 'function') {
      modal.cacheDOMElements();
    } else {
      modal.modalButton = modal.querySelector('[data-modal-button]');
      modal.modalTemplate = modal.querySelector('template');
    }

    this._root.appendChild(modal);
    this._imageModal = modal;

    if (typeof modal.cacheDOMElements === 'function') modal.cacheDOMElements();

    modal.modalButton   = modal.modalButton   || modal.querySelector('[data-modal-button]');
    modal.modalTemplate = modal.modalTemplate || modal.querySelector('template');

    this._imageModalTrigger = modal.modalButton || null;
    return modal;
  }

  _buildReviewFull(review){
    const left  = this._buildLeft(review);
    const right = this._buildRight(review);
    return `
      <article class="bv-item bv-item--modal">
        ${right}
      </article>
    `;
  }

  async _openImageModal(url, alt='', reviewId=''){
    await this._ensureImageModal();

    if (!this._imageModal) {
      if (url) window.open(url, '_blank', 'noopener');
      console.warn('BV Reviews: theme modal unavailable, falling back to direct image open.');
      return;
    }

    const tpl = this._imageModal.querySelector('template');
    if (!tpl) {
      if (url) window.open(url, '_blank', 'noopener');
      console.warn('BV Reviews: modal template missing.');
      return;
    }
    const imgNode = tpl.content.querySelector('.bv-image-full');
    const detailNode = tpl.content.querySelector('.bv-review-detail');
    if (imgNode) {
      imgNode.src = url;
      imgNode.alt = alt;
    }
    if (detailNode) detailNode.innerHTML = '<div class="bv-loading">Loading review…</div>';

    let review = (reviewId && this._reviewsById.get(reviewId)) || null;
    if (!review && reviewId){
      try { review = await this._fetchReviewById(reviewId); } catch(e) { /* ignore */ }
    }

    if (!review){
      try { review = await this._fetchReviewByPhotoUrl(url); } catch(e) { /* ignore */ }
    }

    if (detailNode){
      detailNode.innerHTML = review ? this._buildReviewFull(review)
                                    : '<div class="bv-empty-state">Review not available.</div>';
    }

    if (this._imageModalTrigger && typeof this._imageModalTrigger.click === 'function') {
      this._imageModalTrigger.click();
    } else if (typeof this._imageModal.showModal === 'function') {
      const evt = { preventDefault(){}, stopPropagation(){}, currentTarget: this._imageModal };
      this._imageModal.showModal(evt);
    } else if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }

  /* ---------- Utils ---------- */
  _debounce(fn,ms){ let t; return (...args)=>{ clearTimeout(t); t=setTimeout(()=>fn.apply(this,args),ms); }; }

  /* ---------- Filters UI ---------- */
  async _buildFiltersUI(){
    const productConfig = await this._fetchJSON(this._buildProductURL());
    const root = this.el.filtersRoot;
    root.innerHTML = '';

    const product = (productConfig.Results && productConfig.Results[0]) ||
                    (productConfig.Includes?.Products && Object.values(productConfig.Includes.Products)[0]) || null;
    const rs = product?.ReviewStatistics || {};
    const ratingDist = (rs.RatingDistribution || []).slice().sort((a,b)=>b.RatingValue - a.RatingValue);

    if (ratingDist.length){
      const dd = document.createElement('custom-dropdown');
      dd.setAttribute('data-label', 'Rating');
      dd.setAttribute('data-placeholder', 'RATING');
      root.appendChild(dd);

      dd.setOptions(ratingDist.map(r => ({
        value: String(r.RatingValue),
        label: '★'.repeat(r.RatingValue),
        count: r.Count
      })));

      dd.addEventListener('change', (e)=>{
        e.stopImmediatePropagation();
        this._state.filters.ratingEq = e.detail.value || '';
        this._resetAndReload();
      });
    }

    let cdvDist = rs.ContextDataDistribution || {};
    if (!Object.keys(cdvDist).length){
      const firstPage = await this._fetchJSON(this._buildReviewsURL(0));
      const seen = {};
      (firstPage.Results||[]).forEach(rv=>{
        const cdv = rv.ContextDataValues || {};
        Object.keys(cdv).forEach(k=>{
          const keyLower = k.toLowerCase();
          seen[keyLower] = seen[keyLower] || new Set();
          const label = cdv[k].ValueLabel || cdv[k].Value;
          if (label) seen[keyLower].add(label);
        });
      });
      Object.keys(seen).forEach(k=>{
        cdvDist[k] = {
          DimensionLabel: k.replace(/_/g,' ').replace(/([a-z])([A-Z])/g,'$1 $2'),
          ValueDistribution: Array.from(seen[k]).map(v=>({ ValueLabel: v, Count: '' }))
        };
      });
    }

    Object.keys(cdvDist).forEach((key)=>{
      const id = key.toLowerCase();
      const dim = cdvDist[key];
      const values = (dim?.ValueDistribution || dim?.Values || []);
      if (!values.length) return;
      let label = (dim?.DimensionLabel || dim?.Id || key).replace(/_/g,' ').replace(/([a-z])([A-Z])/g,'$1 $2');

      const dd = document.createElement('custom-dropdown');
      dd.setAttribute('data-label', label.toUpperCase());
      dd.setAttribute('data-placeholder', label.toUpperCase());
      root.appendChild(dd);

      dd.setOptions(values.map(v => ({
        value: String(v.ValueLabel || v.Value),
        label: String(v.ValueLabel || v.Value),
        count: v.Count === '' ? undefined : v.Count
      })));

      dd.addEventListener('change', (e)=>{
        e.stopImmediatePropagation();
        const val = e.detail.value || '';
        if (val) this._state.filters.cdv[id] = val;
        else delete this._state.filters.cdv[id];
        this._resetAndReload();
      });
    });
  }

  /* ---------- Load & Pagination ---------- */
  async _loadMore(){
    this.el.loadMore.disabled = true;
    try{
      const reviews = await this._fetchJSON(this._buildReviewsURL(this._state.offset));
      const results = reviews.Results || [];
      this._state.total = reviews.TotalResults || this._state.total;

      this._renderReviews(results);
      if (!this._state.firstSummaryDone){ this._renderSummary(reviews); this._state.firstSummaryDone = true; }

      if (this._cfg.ENABLE_MEDIA_STRIP){
        const photos = await this._fetchJSON(this._buildPhotosURL());
        this._renderStrip(photos.Results || []);
      }

      this._state.offset += this._cfg.Page_SIZE || this._cfg.PAGE_SIZE; // keep compat if typo
      if (this._state.offset >= this._state.total) this.el.loadMore.style.display = 'none';

      this.el.empty.hidden = !!results.length || this._state.offset < this._state.total;
    }catch(e){
      console.error(e);
      this.el.error.hidden = false;
    }finally{
      this.el.loadMore.disabled = false;
    }
  }

  _resetAndReload(){
    this.el.list.innerHTML = '';
    this.el.empty.hidden = true;
    this.el.error.hidden = true;
    this._state.offset = 0;
    this._state.total = 0;
    this._state.firstSummaryDone = false;
    this.el.loadMore.style.display = '';
    this._loadMore();
  }

  /* ---------- Builders ---------- */
  _buildProductURL(){ return this._makeUrl('products', { 
    filter: `id:eq:${this._cfg.PRODUCT_ID}`,
    filter_reviews: `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`,
    filter_questions: `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`,
    filter_answers: `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`,
    filter_reviewcomments: `contentlocale:eq:${this._cfg.LOCALE},${this._cfg.LOCALE}`,
    filteredstats: 'Reviews, questions, answers',
    stats: 'Reviews, questions, answers'
  }); }
  _makeUrl(resource, extraParams){
    const p = this._qs({ ...this._commonParams(), resource, ...extraParams });
    return `${this._cfg.BASE}/${resource}.json?${p.toString()}`;
  }
}

/* register element */
customElements.define('bv-reviews', BvReviews);
